import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";

export default function Navbar() {
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const onHome = location.pathname === "/";

    const sectionLinks = [
        { label: "How It Works", id: "how-it-works" },
        { label: "Features", id: "features" },
        { label: "Contact", id: "contact" },
    ];

    // Close mobile menu on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const goToSection = (id) => {
        setOpen(false);
        if (onHome) {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate(`/#${id}`);
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 60);
        }
    };

    const goToLogo = () => {
        setOpen(false);
        if (onHome) {
            const el = document.getElementById("hero");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
            navigate("/");
        }
    };

    const goToPackages = () => {
        setOpen(false);
        navigate("/packages");
    };

    const goToPortfolio = () => {
        setOpen(false);
        navigate("/portfolio");
    };

    return (
        <header
            data-testid="site-navbar"
            className="fixed top-0 left-0 right-0 z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
                <button
                    data-testid="navbar-logo"
                    onClick={goToLogo}
                    className="flex items-center gap-3 group"
                    aria-label="Return home"
                >
                    <img
                        src={LOGO_URL}
                        alt="RecoveryDesk — return home"
                        className="h-10 w-10 object-cover rounded-sm ring-1 ring-[#D4AF37]/30 group-hover:ring-[#D4AF37]/70 transition-all duration-500"
                    />
                </button>

                {/* Desktop nav */}
                <nav className="hidden md:flex items-center gap-10">
                    {sectionLinks.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-${l.id}`}
                            onClick={() => goToSection(l.id)}
                            className="text-[11px] uppercase tracking-[0.22em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors duration-300"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-packages"
                        onClick={goToPackages}
                        className={`text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                            location.pathname === "/packages"
                                ? "text-[#D4AF37]"
                                : "text-[#C4C4C4] hover:text-[#D4AF37]"
                        }`}
                    >
                        Packages
                    </button>
                    <button
                        data-testid="nav-portfolio"
                        onClick={goToPortfolio}
                        className={`text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                            location.pathname === "/portfolio"
                                ? "text-[#D4AF37]"
                                : "text-[#C4C4C4] hover:text-[#D4AF37]"
                        }`}
                    >
                        Project &amp; Testimonials
                    </button>
                    <button
                        data-testid="nav-questionnaire"
                        onClick={() => { setOpen(false); navigate("/questionnaire"); }}
                        className={`text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                            location.pathname === "/questionnaire"
                                ? "text-[#D4AF37]"
                                : "text-[#C4C4C4] hover:text-[#D4AF37]"
                        }`}
                    >
                        Questionnaire
                    </button>
                </nav>

                {/* Desktop CTA */}
                <button
                    data-testid="navbar-contact-btn"
                    onClick={() => goToSection("contact")}
                    className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-semibold btn-gold-hover hover:bg-[#E5C158]"
                >
                    Contact Us
                </button>

                {/* Mobile hamburger */}
                <button
                    data-testid="navbar-mobile-toggle"
                    onClick={() => setOpen((v) => !v)}
                    aria-label={open ? "Close menu" : "Open menu"}
                    className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-[#D4AF37]/40 text-[#D4AF37]"
                >
                    {open ? (
                        <X className="w-5 h-5" strokeWidth={1.75} />
                    ) : (
                        <Menu className="w-5 h-5" strokeWidth={1.75} />
                    )}
                </button>
            </div>

            {/* Mobile menu panel */}
            {open ? (
                <div
                    data-testid="navbar-mobile-menu"
                    className="md:hidden fixed inset-x-0 top-20 h-[calc(100vh-5rem)] border-t border-white/[0.05] bg-black overflow-y-auto"
                >
                    <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1">
                        {sectionLinks.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-mobile-${l.id}`}
                                onClick={() => goToSection(l.id)}
                                className="text-left text-[12px] uppercase tracking-[0.22em] text-[#C4C4C4] hover:text-[#D4AF37] py-4 border-b border-white/[0.05]"
                            >
                                {l.label}
                            </button>
                        ))}
                        <button
                            data-testid="nav-mobile-packages"
                            onClick={goToPackages}
                            className={`text-left text-[12px] uppercase tracking-[0.22em] py-4 border-b border-white/[0.05] ${
                                location.pathname === "/packages"
                                    ? "text-[#D4AF37]"
                                    : "text-[#C4C4C4] hover:text-[#D4AF37]"
                            }`}
                        >
                            Packages
                        </button>
                        <button
                            data-testid="nav-mobile-portfolio"
                            onClick={goToPortfolio}
                            className={`text-left text-[12px] uppercase tracking-[0.22em] py-4 border-b border-white/[0.05] ${
                                location.pathname === "/portfolio"
                                    ? "text-[#D4AF37]"
                                    : "text-[#C4C4C4] hover:text-[#D4AF37]"
                            }`}
                        >
                            Project &amp; Testimonials
                        </button>
                        <button
                            data-testid="nav-mobile-questionnaire"
                            onClick={() => { setOpen(false); navigate("/questionnaire"); }}
                            className={`text-left text-[12px] uppercase tracking-[0.22em] py-4 border-b border-white/[0.05] ${
                                location.pathname === "/questionnaire"
                                    ? "text-[#D4AF37]"
                                    : "text-[#C4C4C4] hover:text-[#D4AF37]"
                            }`}
                        >
                            Questionnaire
                        </button>
                        <button
                            data-testid="navbar-mobile-cta"
                            onClick={() => goToSection("contact")}
                            className="mt-6 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E5C158]"
                        >
                            Contact Us
                        </button>
                    </nav>
                </div>
            ) : null}
        </header>
    );
}
